"use client";

import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import styles from "./styles.module.css";
import Input from "../../modules/Input/Input";
import { Checkbox } from "../../../ui/checkbox";
import Button from "../../modules/Button/Button";
import Cart from "./items";
import toast from "react-hot-toast";

// COMPONENTS
import EmailView from "./email";
import { getDeviceTypes } from "../../../../services/inventory/detail";

const OrderBulk = forwardRef(
  ({ addType, setAddType, emailPreviewRef }, ref) => {
    const [devices, setDevices] = useState([]);
    const [deviceTypes, setDeviceTypes] = useState([]); // Dynamic device types from API
    const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(true);

    const [errors, setErrors] = useState({ type: "", quantity: "" });

    const [device, setDevice] = useState({
      type: "", // this will be the selected name
      quantity: "",
    });

    useImperativeHandle(ref, () => ({
      getDevices: () => devices,
      resetForm: () => {
        setDevices([]);
        setDevice({ type: "", quantity: "" });
        setErrors({ type: "", quantity: "" });
      },
    }));

    useEffect(() => {
      const fetchDeviceTypes = async () => {
        try {
          setLoadingDeviceTypes(true);
          const response = await getDeviceTypes();
          if (response && Array.isArray(response)) {
            setDeviceTypes(response);
          }
        } catch (error) {
          console.error("Error fetching device types:", error);
          toast.error("Failed to load device types");
        } finally {
          setLoadingDeviceTypes(false);
        }
      };

      fetchDeviceTypes();
    }, []);

    const handleInsert = () => {
      let valid = true;
      let newErrors = { type: "", quantity: "" };

      if (!device.type) {
        newErrors.type = "Device type is required";
        valid = false;
      }
      if (!device.quantity) {
        newErrors.quantity = "Quantity is required";
        valid = false;
      } else if (isNaN(device.quantity) || Number(device.quantity) <= 0) {
        newErrors.quantity = "Enter a valid number";
        valid = false;
      }

      setErrors(newErrors);

      if (!valid) return;

      const selectedType = deviceTypes.find((dt) => dt.name === device.type);
      if (!selectedType) return toast.error("Invalid device type");

      const itemIndex = devices.findIndex(
        (_device) => _device.id === selectedType.id
      );
      if (itemIndex !== -1) {
        return toast.error("Item already in list");
      }

      setDevices((prev) => [
        ...prev,
        {
          id: selectedType.id, //  id save karo
          name: selectedType.name, //  name bhi save karo
          quantity: device.quantity,
        },
      ]);

      setDevice({ type: "", quantity: "" });
      setErrors({ type: "", quantity: "" });
    };

    const removeDevice = (id) => {
      setDevices((prev) => prev.filter((_device) => _device.id !== id));
    };

    const deviceTypeHandler = (name) => {
      setDevice((prev) => ({ ...prev, type: name }));
      setErrors((prev) => ({ ...prev, type: "" }));
    };

    if (addType === 3) {
      return <EmailView ref={emailPreviewRef} devices={devices} />;
    }
    if (addType === 2) {
      return (
        <>
          <section className={styles.container}>
            <h3>Order Details</h3>
            <section className={styles.orderInfoContainer}>
              {/* Device Type */}
              <section className={styles.inputField}>
                <label className={styles.label}>Device Type</label>
                <main className={styles.deviceTypeContainer}>
                  {loadingDeviceTypes ? (
                    <div>Loading device types...</div>
                  ) : (
                    deviceTypes.map((_deviceType) => {
                      const isSelected = devices.some(
                        (d) => d.id === _deviceType.id
                      );
                      return (
                        <section
                          key={_deviceType.id}
                          className={styles.deviceTypeCheckbox}
                        >
                          <Checkbox
                            className={styles.checkbox}
                            size="large"
                            value={_deviceType.name}
                            checked={device.type === _deviceType.name}
                            onCheckedChange={() =>
                              deviceTypeHandler(_deviceType.name)
                            }
                            disabled={isSelected} //  disable if already added
                          />
                          <label className={styles.checkboxLabel}>
                            {_deviceType.name}
                          </label>
                        </section>
                      );
                    })
                  )}
                </main>
                {errors.type && <p className={styles.error}>{errors.type}</p>}
              </section>

              {/* Quantity */}
              <section className={styles.inputField}>
                <Input
                  type="number"
                  min="0"
                  placeholder="number of devices"
                  label="Quantity"
                  name="quantity"
                  value={device.quantity}
                  onChange={(e) =>
                    setDevice((prev) => ({
                      ...prev,
                      quantity: e.target.value,
                    }))
                  }
                />
                {errors.quantity && (
                  <p className={styles.error}>{errors.quantity}</p>
                )}
              </section>

              {/* Add Button */}
              <section className={styles.actionContainer}>
                <Button size="small" onClick={() => handleInsert()}>
                  Add Item
                </Button>
              </section>
            </section>
          </section>
          <Cart key={device.type} devices={devices} remove={removeDevice} />
        </>
      );
    }
    return null;
  }
);

OrderBulk.displayName = "OrderBulk";

export default OrderBulk;
